import styled from 'styled-components';
import { color, typeStyle } from '@m/alchemy-ui/theme';

export const CardWrapper = styled.article<{ $hasTopBadge?: boolean }>`
  display: flex;
  flex-direction: row;
  position: relative;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.2);
  transition: box-shadow 0.5s ease-in-out;
  overflow: hidden;
  min-height: ${({ $hasTopBadge }) => ($hasTopBadge ? '192px' : '160px')};

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  }

  @media (max-width: 480px) {
    flex-direction: column;
    min-height: auto;
  }
`;

export const ContentWrapper = styled.div<{ $hasTopBadge?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: ${({ $hasTopBadge }) => ($hasTopBadge ? '44px 16px 12px' : '12px 16px')};
  overflow: hidden;
`;

export const FlaggedBadge = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 101;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 5px 5px 0 0;
  background-color: #d32f2f;
  clip-path: polygon(0 0, 100% 0, 100% 100%);
  color: #fff;
  cursor: default;

  svg {
    display: block;
  }
`;

export const Thumbnail = styled.figure<{ $hasTopBadge?: boolean }>`
  width: 120px;
  min-height: 120px;
  overflow: hidden;
  flex-shrink: 0;
  margin-top: ${({ $hasTopBadge }) => ($hasTopBadge ? '32px' : '0')};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (max-width: 480px) {
    width: 100%;
    height: 60vw;
    max-height: 200px;
  }
`;

export const ThumbnailPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background-color: #f0f0f0;
`;

export const LabelWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 4px;
`;

export const StatusLabel = styled.div`
  padding: 5px 10px;
  border-radius: 5px;
  background: #5c5c5c;
  color: #fff;
  font-size: 12px;
  line-height: 12px;
  margin-right: 3px;
  margin-bottom: 3px;
`;

export const HiddenLabel = styled.div`
  padding: 5px 10px;
  border-radius: 5px;
  background: transparent;
  border: 1px solid #ccc;
  color: #888;
  font-size: 12px;
  line-height: 12px;
  margin-right: 3px;
  margin-bottom: 3px;
`;

export const PrivateLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border-radius: 5px;
  background-color: #00163b;
  color: #fff;
  font-size: 12px;
  line-height: 12px;
  margin-right: 3px;
  margin-bottom: 3px;

  svg {
    width: 12px;
    height: 12px;
  }
`;

export const IdeaTitle = styled.h2`
  margin: 0;
  margin-bottom: 8px;
  font:
    700 16px 'Nunito',
    -apple-system,
    BlinkMacSystemFont,
    helvetica,
    arial,
    sans-serif;

  a {
    text-decoration: none;
    color: #2c3548;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const IdeaBody = styled.p`
  margin: 0;
  margin-bottom: 12px;
  flex: 1;
  font:
    400 14px / 1.3 'Nunito',
    -apple-system,
    BlinkMacSystemFont,
    helvetica,
    arial,
    sans-serif;
  color: #767676;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;

  a {
    text-decoration: none;
    color: #767676;
    word-break: break-word;
  }
`;

export const DisabledText = styled.span`
  color: #767676;
  cursor: default;
`;

export const AuthorSection = styled.aside<{ $hasTopBadge?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100px;
  flex-shrink: 0;
  padding: ${({ $hasTopBadge }) => ($hasTopBadge ? '48px 16px 16px' : '16px')};
  font:
    400 12px 'Nunito',
    -apple-system,
    BlinkMacSystemFont,
    helvetica,
    arial,
    sans-serif;
  text-align: center;

  @media (max-width: 480px) {
    flex-direction: row;
    width: 100%;
    border-left: none;
    border-top: 1px solid #e0e0e0;
    text-align: left;
  }
`;

export const AvatarFigure = styled.figure`
  margin: 0 0 6px 0;
  flex-shrink: 0;

  @media (max-width: 480px) {
    margin: 0 8px 0 0;
  }
`;

export const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;

  @media (max-width: 480px) {
    align-items: flex-start;
  }
`;

export const AuthorName = styled.div`
  line-height: 1.4;
`;

export const AuthorButton = styled.button`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  padding: 0;
  margin: 0;
  color: ${color('typeLink')};
  text-decoration: none;
  background: transparent;
  border: none;
  cursor: pointer;
  font: inherit;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`;

export const RelativeTime = styled.time`
  font: ${typeStyle('captionM')};
  color: ${color('typeHeader')};
`;

export const Footer = styled.div`
  display: flex;
  min-height: 36px;
  padding-top: 8px;
  margin-top: auto;
  border-top: 1px solid #65788b;
`;

export const VoteSection = styled.div`
  display: flex;
  align-items: center;
`;

export const VoteCount = styled.div<{ $bgColor: string }>`
  background-color: ${({ $bgColor }) => $bgColor};
  border-radius: 8px;
  color: #fff;
  font:
    600 10px 'Nunito',
    -apple-system,
    BlinkMacSystemFont,
    helvetica,
    arial,
    sans-serif;
  padding: 4px 8px 6px;
  min-width: 50px;
  text-align: center;
  text-transform: uppercase;
  position: relative;
`;

export const VoteValue = styled.strong`
  display: block;
  font:
    700 14px / 1.3 'Nunito',
    -apple-system,
    BlinkMacSystemFont,
    helvetica,
    arial,
    sans-serif;
`;

export const ThumbsUpIconWrap = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const VotingClosed = styled.div`
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #888;
  margin-left: 8px;
  white-space: nowrap;
`;

export const CommentLink = styled.span`
  display: flex;
  align-items: center;
  margin-left: auto;
  line-height: 1;
  white-space: nowrap;
  font:
    400 12px 'Nunito',
    -apple-system,
    BlinkMacSystemFont,
    helvetica,
    arial,
    sans-serif;
`;

export const CommentIcon = styled.span`
  display: inline-flex;
  margin-right: 4px;

  svg {
    color: #949494;
  }
`;

export const MilestoneBadge = styled.aside<{ $bgColor: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 99;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 30px 4px 12px;
  background-color: ${({ $bgColor }) => $bgColor};
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  font-size: 12px;
  line-height: 24px;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 20px;
    border: solid transparent;
    border-top-color: ${({ $bgColor }) => $bgColor};
    border-width: 5px;
    margin-left: -5px;
    height: 0;
    width: 0;
    pointer-events: none;
  }

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

export const MilestoneBadgeInfo = styled.span`
  position: absolute;
  right: 5px;
  top: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid currentColor;
  background: transparent;
  color: inherit;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
`;

export const NextPhaseLabel = styled.aside`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 99;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  background-color: #5c5c5c;
  color: #fff;
  font-size: 12px;
  line-height: 24px;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 20px;
    border: solid transparent;
    border-top-color: #5c5c5c;
    border-width: 4px 4px 0 4px;
    height: 0;
    width: 0;
    pointer-events: none;
  }
`;

export const CountdownSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-width: 58px;
  padding: 0 3px;
  margin: 0;
  border-radius: 8px 0 0 8px;
  color: #2c3548;
  font-size: 12px;
`;

export const CountdownValue = styled.strong`
  font-weight: 700;
  line-height: 1;
`;

export const CountdownLabel = styled.span`
  text-transform: uppercase;
`;

export const CountdownPopup = styled.div`
  width: 200px;
  border-radius: 8px;
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.2);
  padding: 10px;
  color: #fff;
`;

export const CountdownPopupTitle = styled.span`
  display: block;
  padding-bottom: 10px;
  text-align: left;
`;

export const CountdownPopupGrid = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

export const CountdownPopupItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 25%;
`;

export const CountdownPopupValue = styled.strong`
  letter-spacing: -1px;
  line-height: 1;
`;

export const CountdownPopupLabel = styled.span``;
